import { type ReactNode, useState, useCallback, useEffect } from "react";
import { OnaLoading } from "@/components/ona/OnaLoading";
import { cn } from "@/components/podkit/lib/cn";

type AnimatedLoadingContainerProps = {
    /** Whether the content is ready to display */
    isLoading: boolean;
    /** The content to display once loading is complete */
    children: ReactNode;
    /** Optional loading message (will have animated ellipsis appended) */
    message?: string;
    /** Size of the Ona animation (default: md / 32px) */
    size?: "sm" | "md" | "lg";
    /** Additional CSS classes for the container */
    className?: string;
};

/**
 * Animated ellipsis that cycles through . .. ...
 */
const AnimatedEllipsis = () => {
    const [dots, setDots] = useState(1);

    useEffect(() => {
        const interval = setInterval(() => {
            setDots((d) => (d % 3) + 1);
        }, 400);
        return () => clearInterval(interval);
    }, []);

    return (
        <span className="inline-block w-4 text-left">
            {".".repeat(dots)}
        </span>
    );
};

type DisplayState = "loading" | "content";

/**
 * AnimatedLoadingContainer wraps content with an Ona loading animation.
 *
 * Flow:
 * 1. Shows OnaLoading (lifting → spinning loop)
 * 2. When isLoading becomes false, OnaLoading plays dropping animation
 * 3. After dropping completes, shows children
 */
export const AnimatedLoadingContainer = ({
    isLoading,
    children,
    message,
    size = "md",
    className,
}: AnimatedLoadingContainerProps) => {
    const [displayState, setDisplayState] = useState<DisplayState>("loading");

    const handleAnimationComplete = useCallback(() => {
        setDisplayState("content");
    }, []);

    // Reset to loading state if isLoading becomes true again
    if (isLoading && displayState !== "loading") {
        setDisplayState("loading");
    }

    const isReady = !isLoading;
    const showContent = displayState === "content";

    // When showing content, render children directly without wrapper
    if (showContent) {
        return <>{children}</>;
    }

    // Loading state with container matching PermissionDenied style
    return (
        <div className={cn("flex flex-1 flex-col items-center justify-center rounded-xl border border-border-subtle p-6 md:p-12 overflow-visible", className)}>
            <div className="flex flex-col items-center gap-4 overflow-visible">
                <OnaLoading isReady={isReady} onComplete={handleAnimationComplete} size={size} />
                {message && (
                    <p className="text-sm text-content-secondary">
                        {message}
                        <AnimatedEllipsis />
                    </p>
                )}
            </div>
        </div>
    );
};
